/**
 * Shows a list of users which are registered for the application.
 * Material design: Table from https://material-ui.com/components/tables/#table
 */

import React, {useContext, useEffect, useState} from 'react'
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import {NavLink} from "react-router-dom";
import useListUsers from "./useListUsers";
import {useStyles} from './UserSection.css'
import {StyledTableCell, StyledTableRow} from "../../../../commons/utils/table/StyledTablePrimary.css";
import AppbarActionButton from "../../../../commons/elements/buttons/AppbarActionButton";
import AddIcon from "@mui/icons-material/Add";
import {AppbarContentContext} from "../../../../commons/routing/appbar/AppbarContentProvider";
import {Delete, Edit, Lock, LockOpen} from "@mui/icons-material";
import {CircularProgress, IconButton} from "@mui/material";
import {useConfirm} from 'material-ui-confirm';
import usePagination from "../../../../commons/utils/usePagination";
import LoadingProgressIndication from "../../../../commons/elements/progress/loading/LoadingProgressIndication";
import UserAuthLogos from "./UserAuthLogos";
import UserCard from "../card/userInfo/UserCard";
import {useIntl} from "react-intl";
import AssociationInfoCard from "../card/associationInfo/AssociationInfoCard";
import {FirebaseAuthContext} from "../../../../commons/firebase/FirebaseAuthProvider";
import GenericFilter from "../../../../commons/elements/search/genericSearch/GenericFilter";


const UsersSection = () => {
    const classes = useStyles();
    const {setCurrentAppbarContent} = useContext(AppbarContentContext)

    // Checking if the deletion operations are allowed
    const {isOperationOnObjectAllowed, signedInUserRoleScopes} = useContext(FirebaseAuthContext);

    // Pagination for the table handling
    const {page, rowsPerPage, handleChangePage, handleChangeRowsPerPage} = usePagination()

    // Hook providing the list user functionality
    const {
        users,
        loading,
        handleDeleteUser,
        deletionInProgress,
        deletionAttemptedUser,
        disableUser,
        enableUser
    } = useListUsers()

    // State which holds the courses according to the current filter
    const [filteredUsers, setFilteredUsers] = useState([])

    // Confirmation dialog
    const confirm = useConfirm();

    // Internationalization for simple strings
    const intl = useIntl();

    // Setting the appbar content
    useEffect(() => {
        setFilteredUsers(users)

        setCurrentAppbarContent({
            header: intl.formatMessage({id: 'app.users.overview.appbar.header', defaultMessage: 'Users'}),
            description: intl.formatMessage({
                id: 'app.users.overview.appbar.description',
                defaultMessage: 'Manage the users of the system'
            }),
            actions: <>
                <AppbarActionButton
                    label={intl.formatMessage({
                        id: 'app.users.overview.appbar.actions.new',
                        defaultMessage: 'New User'
                    })}
                    component={NavLink}
                    to='/admin/users/new'
                    icon={<AddIcon/>}/>
            </>,
            searchContainer: <GenericFilter objects={users}
                                            onResultsChanged={(results) => setFilteredUsers(results)}
                                            filterDefinitions={[
                                                {
                                                    type: 'select',
                                                    id: 'app.users.filter.name',
                                                    options: [...new Set(users.map((u) => u.firstName && u.secondName
                                                        ? `${u.firstName} ${u.secondName}`
                                                        : u.displayName))],
                                                    getOptionLabel: (n) => n,
                                                    condition: (object, filter) => filter?.includes(object.firstName && object.secondName
                                                        ? `${object.firstName} ${object.secondName}`
                                                        : object.displayName) || filter?.length === 0
                                                },
                                                {
                                                    type: 'select',
                                                    id: 'app.users.filter.email',
                                                    options: [...new Set(users.map((u) => u.email))],
                                                    getOptionLabel: (e) => e,
                                                    condition: (object, filter) => filter?.includes(object.email) || filter?.length === 0
                                                },
                                                {
                                                    type: 'select',
                                                    id: 'app.users.filter.associations',
                                                    options: getAllAssociationsOfUsers(),
                                                    getOptionLabel: (association) =>
                                                        intl.formatMessage({
                                                            id: `app.ids.association.${association}`,
                                                            defaultMessage: association
                                                        }),
                                                    condition: (object, filter) => filter.find((roleScope) => object.roleScopes.includes(roleScope)) || filter?.length === 0
                                                },
                                                {
                                                    type: 'select',
                                                    id: 'app.users.filter.disciplines',
                                                    options: getAllDisciplinesOfUsers(),
                                                    getOptionLabel: (discipline) =>
                                                        intl.formatMessage({
                                                            id: `app.ids.discipline.${discipline}`,
                                                            defaultMessage: discipline
                                                        }),
                                                    condition: (object, filter) => filter.find((fd) => getAllDisciplinesOfUser(object).includes(fd)) || filter?.length === 0
                                                },
                                            ]}/>,
            progress: deletionInProgress
        })
    }, [deletionInProgress, users])

    const getAllAssociationsOfUsers = () => {
        if (!users) {
            return []
        }

        const aList = []
        users.forEach((u) => {
            aList.push(...u.roleScopes)
        })
        return [...new Set(aList)]
    }

    const getAllDisciplinesOfUsers = () => {
        if (!users) {
            return []
        }

        const dList = []
        users.forEach((u) => {
            dList.push(...getAllDisciplinesOfUser(u))
        })
        return [...new Set(dList)]
    }

    const getAllDisciplinesOfUser = (user) => {
        const uDisciplines = []

        if (user.associationInfo) {
            for (let association in user.associationInfo) {
                uDisciplines.push(...user.associationInfo[association].disciplines)
            }
        }
        return [...new Set(uDisciplines)]
    }

    /* The signed-in user's role must be a superset of the scopes of the user to be deleted.
       This way we go sure that mightier users are not deleted by less-mightier ones. */
    const hasEnoughRoleScopesToDelete = (userToBeDeleted) => {

        // Role scopes are actually always not-null - however during migration phases not all users had this pre-requisite
        return userToBeDeleted.roleScopes?.every((userScope) => signedInUserRoleScopes.includes(userScope))
    }

    // Show loading indication while loading the list of users
    if (loading) {
        return <LoadingProgressIndication
            text={intl.formatMessage({
                id: 'app.users.overview.loading',
                defaultMessage: 'Loading users...'
            })}/>
    }

    const handleDisableUser = (user) => {
        confirm({title: 'Disable User', description: 'Disable user with email ' + user.email + '?'})
            .then(() => disableUser(user))
            .catch(() => null)
    }

    const handleEnableUser = (user) => {
        confirm({title: 'Enable User', description: 'Enable user with email ' + user.email + '?'})
            .then(() => enableUser(user))
            .catch(() => null)
    }

    // Shows the table containing the users
    return (
        <Paper className={classes.root}>
            <TableContainer className={classes.container}>
                <Table stickyHeader aria-label="sticky table">

                    {/* Table header */}
                    <TableHead>
                        <TableRow>
                            <StyledTableCell align={"center"} width={"5%"} key='signin'></StyledTableCell>
                            <StyledTableCell align={"center"} width={"40%"} key='profile'>
                                {intl.formatMessage({
                                    id: 'app.users.overview.table.column.profileInformation',
                                    defaultMessage: 'Profile Information'
                                })}
                            </StyledTableCell>
                            <StyledTableCell align={"center"} width={"40%"} key='disciplinesExperience'>
                                {intl.formatMessage({
                                    id: 'app.users.overview.table.column.disciplinesExperience',
                                    defaultMessage: 'Disciplines & Experiences'
                                })}
                            </StyledTableCell>
                            <StyledTableCell align={"right"} width={"15%"} key='action'>
                                {intl.formatMessage({
                                    id: 'app.users.overview.table.column.action',
                                    defaultMessage: 'Actions'
                                })}
                            </StyledTableCell>
                        </TableRow>
                    </TableHead>

                    {/* Table rows */}
                    <TableBody>
                        {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                            return (
                                <StyledTableRow hover key={row.uid}>
                                    <StyledTableCell key='authProvider'>
                                        <UserAuthLogos providers={row.providers} emailVerified={row.emailVerified}/>
                                    </StyledTableCell>
                                    <StyledTableCell key='profile'>
                                        <UserCard user={row} headerSize={18} textSize={16}/>
                                    </StyledTableCell>
                                    <StyledTableCell key='disciplinesExperience'>
                                        <AssociationInfoCard associationInfo={row.associationInfo}/>
                                    </StyledTableCell>
                                    <StyledTableCell align={"right"} key='action'>
                                        <IconButton component={NavLink} to={'/admin/users/' + row.uid + '/edit'} size="large">
                                            <Edit/>
                                        </IconButton>
                                        <IconButton
                                            onClick={() => handleDeleteUser(row)}
                                            disabled={!hasEnoughRoleScopesToDelete(row) || !isOperationOnObjectAllowed(row)}
                                            size="large">
                                            {row.uid === deletionAttemptedUser
                                                ? <CircularProgress style={{height: 20, width: 20}}/>
                                                : <Delete/>}
                                        </IconButton>
                                        {
                                            !row.disabled
                                                ? <IconButton
                                                onClick={() => handleDisableUser(row)}
                                                disabled={!hasEnoughRoleScopesToDelete(row) || !isOperationOnObjectAllowed(row)}
                                                size="large">
                                                    <Lock/>
                                                </IconButton>
                                                : <IconButton
                                                onClick={() => handleEnableUser(row)}
                                                disabled={!hasEnoughRoleScopesToDelete(row) || !isOperationOnObjectAllowed(row)}
                                                size="large">
                                                    <LockOpen/>
                                                </IconButton>
                                        }

                                    </StyledTableCell>
                                </StyledTableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination section */}
            <TablePagination
                rowsPerPageOptions={[10, 25, 100]}
                component="div"
                count={filteredUsers.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Paper>
    );
}

export default UsersSection
